import { Injectable } from '@angular/core';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
 
export interface ExcelColumn {
  key: string;
  header: string;
  width?: number;
}
 
const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
 
@Injectable({ providedIn: 'root' })
export class ExcelExportService {
 
  async exportToExcel(
    columns: ExcelColumn[],
    rows: any[],
    fileName: string,
    sheetName = 'Sheet1'
  ): Promise<void> {
    const workbook = new ExcelJS.Workbook();
    // Excel sheet names are max 31 chars and cannot contain : \ / ? * [ ]
    const safeName = sheetName.replace(/[:\\/?*[\]]/g, '').slice(0, 31) || 'Sheet1';
    const sheet = workbook.addWorksheet(safeName);
 
    sheet.columns = columns.map(col => ({
      key: col.key,
      header: col.header,
      // Review Name auto-fits its content; others use the default width but never clip the header.
      width: col.width
        ?? (col.header === 'Review Name'
          ? this.getColumnWidth(col.header, rows.map(row => row?.[col.key]))
          : Math.max(15, this.getColumnWidth(col.header, []) + 2)),
    }));
    rows.forEach(row => {
      const values: Record<string, any> = {};
      columns.forEach(col => {
        const value = row?.[col.key];
        values[col.key] = value == null ? '' : value;
      });
      sheet.addRow(values);
    });
 
    sheet.eachRow(row => {
      row.eachCell(cell => {
        cell.font = { name: 'Aptos Narrow', size: 11, bold: row.number === 1 };
        cell.alignment = { horizontal: 'left', vertical: 'top' };
      });
      row.alignment = { horizontal: 'left', vertical: 'top' };
    });
 
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: XLSX_MIME });
    saveAs(blob, fileName.toLowerCase().endsWith('.xlsx') ? fileName : `${fileName}.xlsx`);
  }

  /** Returns the Excel column width that exactly fits the longest exported line. */
  private getColumnWidth(header: string, values: any[]): number {
    const lines = [header, ...values.flatMap(value => String(value ?? '').split(/\r?\n/))];
    const context = typeof document === 'undefined'
      ? null
      : document.createElement('canvas').getContext('2d');

    if (!context) {
      return Math.max(1, ...lines.map(line => line.length));
    }

    // ExcelJS size 11 is points; canvas needs pixels (pt * 96 / 72).
    const fontPx = 11 * 96 / 72;
    const maxPixels = lines.reduce((widest, line, index) => {
      context.font = `${index === 0 ? 'bold ' : ''}${fontPx}px "Aptos Narrow", "Arial Narrow", sans-serif`;
      return Math.max(widest, context.measureText(line).width);
    }, 0);

    // One Excel width unit is ~7px; add ~5px for the cell's built-in padding.
    return Math.max(1, (maxPixels + 5) / 7);
  }
 
  /** Builds a name like ActionPlan_ExportAll_02Sep2026 */
  buildFileName(prefix: string, date: Date = new Date()): string {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const dd = String(date.getDate()).padStart(2, '0');
    return `${prefix}_${dd}${months[date.getMonth()]}${date.getFullYear()}`;
  }
}